// plugins/currencyPlugin.js
import Vue from 'vue'
import axios from 'axios'

/**
 * A plain object, and nothing else: an array, a string, a number, null and undefined are all rejected.
 * The rates this plugin holds are always a map of currency code to rate, so anything else is an answer
 * or a cache entry that cannot be used.
 */
const isRatesObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const CurrencyPlugin = {
  install(Vue) {
    const state = Vue.observable({
      exchangeRates: {},
      // currencies: [],
      baseCurrency: null,
      isLoaded: false,
    });

    const loadRates = async (base = 'USD') => {
      const cacheKey = `exchangeRatesCache_${base}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        // A cached entry is used only when it really carries rates. `/getRates` answers
        // {"status":false,"message":"Couldn't load"} with no `rates` key for every request made while
        // the user's selected entity is not a customer (ExchangeRateController::getRates), and this
        // plugin used to cache that answer as {"updated_at":"..."} -- `JSON.stringify` drops the
        // undefined `rates`. Reading such an entry back set `state.exchangeRates` to undefined on
        // every later page load and never asked the API again. The entry is dropped and the API asked,
        // which is what heals a browser that already holds one.
        let parsed = null;
        try {
          parsed = JSON.parse(cached);
        } catch (e) {
          parsed = null;
        }

        if (isRatesObject(parsed) && isRatesObject(parsed.rates)) {
          state.exchangeRates = parsed.rates;
          // state.currencies = parsed.currencies;
          state.baseCurrency = base;
          state.isLoaded = true;
          return;
        }

        localStorage.removeItem(cacheKey);
      }

      try {
        const res = await axios.get('/api/assetflow/exchange_rates/getRates', {
            params: { base }
        });

        const refused = !res || !res.data || res.data.status === false || !isRatesObject(res.data.rates);

        if (refused) {
          // Nothing is written: the state keeps the rates it already had, `isLoaded` stays false so a
          // later init() -- after the user switches back into a customer context -- asks again, and no
          // cache entry is left behind to poison the next page load.
          console.warn(
            "Exchange rates were refused for base " + base + ", so amounts are shown unconverted",
            res && res.data ? res.data : res
          );
          return;
        }

        state.exchangeRates = res.data.rates;
        // state.currencies = res.data.currencies;
        state.baseCurrency = base;
        state.isLoaded = true;
        localStorage.setItem(cacheKey, JSON.stringify({
          rates: res.data.rates,
          // currencies: res.data.currencies,
          updated_at: new Date(),
        }));
      } catch (err) {
        console.error("Failed to load exchange rates", err);
      }
    };

    Vue.prototype.$currency = {
      state,
      async init(base = 'USD') {
        if (!state.isLoaded || state.baseCurrency !== base) {
          await loadRates(base);
        }
      },
      convert(amount, toCurrency) {
        // The amount is drawn unconverted whenever no rate is known: no rates object at all, no rate
        // for this currency, or a rate that is not a usable number. This method is called from every
        // money cell the asset pages draw (currencyHelper.mixin.js `convertValue()`), so a throw here
        // is a whole page lost to the error boundary.
        if (!isRatesObject(state.exchangeRates)) return amount;

        const rate = Number(state.exchangeRates[toCurrency]);
        if (!isFinite(rate) || rate <= 0) return amount;

        return amount * rate;
      },
    };
  }
};

Vue.use(CurrencyPlugin);
